import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Text, Flex, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { BillingChangeRequestType } from "~/graphql/supergraphEnums";
import {
  useCreateChangeRequestMutation,
  useGeneratePresignedDocumentPutUrlMutation,
  useGetBillingCaseDetailLazyQuery,
  useGetGpChangeRequestLazyQuery,
  useSubmitGpChangeRequestMutation,
} from "~/graphql/supergraphHooks";
import {
  ENTITY_HAS_OPEN_CR_ERROR_CODE,
  EXISTING_REQUEST_TYPE_FALLBACK_LABEL,
  EXISTING_REQUEST_TYPE_LABELS,
  FAMILY_HAS_OPEN_CR_ERROR_CODE,
} from "~/pages/Billing/constants";
import { formatDateForApi } from "~/pages/Billing/contexts/BillingDateContext";
import { formatDate } from "~/pages/Billing/utils";
import { CaseEntitiesGrid } from "./CaseEntitiesGrid";
import { ChangeInspectionModal } from "./ChangeInspectionModal";
import { NOT_ASSIGNED_LABEL } from "./constants";
import * as styles from "./styles_vanilla.css";
import { CaseTypeDisplay } from "./types";
import {
  EUM_CALCULATION_VALUES_ROW_LABEL,
  EUM_VALUE_OVERRIDE_ROW_LABEL,
} from "./utils/eumProposedChangesBuilders";
import { buildProposedChangesFromGpChangeRequest } from "./utils/gpChangeRequestProposedChangesBuilders";
import { uploadGpSupportingDocument } from "./utils/gpDocumentUpload";
import {
  buildEumCalcEditItem,
  buildEumOverrideItem,
  isEumPendingChange,
  wouldMixChangeTypes as evaluateChangeTypeMixing,
} from "./utils/gpEumChangeItemBuilders";
import type {
  BulkActionChangeData,
  CreateChangeRequestData,
  EntitiesTabActions,
  GridActions,
  PendingFCCChange,
  ProposedChangesResult,
  StoredProposedChange,
  StoredFamilyProposedChange,
  SubmittedPendingChange,
  SubmitGpEumChangesArgs,
} from "./types";
import type {
  CaseStatus,
  EumChangeItemInput,
  FamilyChangeRequestItemAction,
  GetBillingCaseDetailQuery,
} from "~/graphql/supergraphTypes";

type EntitiesTabProps = {
  isChecker: boolean;
  clientId: number;
  onLocalChangesUpdate?: (hasChanges: boolean, hasPendingEumChanges: boolean) => void;
  onEntitiesActionsReady?: (actions: EntitiesTabActions) => void;
  pendingChangeRequestIds?: string[];
  pendingGpChangeRequestIds?: string[];
};

const EMPTY_PENDING_IDS: string[] = [];

const buildProposedChangesFromCaseDetail = (
  changeRequestId: string,
  caseDetail: GetBillingCaseDetailQuery["getCaseDetail"],
): ProposedChangesResult => {
  const entityMap = new Map<number, StoredProposedChange[]>();
  const entityCounts = new Map<number, number>();
  const familyMap = new Map<string, StoredFamilyProposedChange>();
  const familyCounts = new Map<string, number>();
  const billingGroupChanges = new Map<string, StoredProposedChange[]>();

  const familyName = caseDetail.familyDisplayName ?? "";
  const requestType = caseDetail.requestType;

  if (requestType === BillingChangeRequestType.FirstCapitalCallDate) {
    const fccChanges = caseDetail.fccProposedChanges ?? [];
    for (const fccChange of fccChanges) {
      const storedChange: StoredProposedChange = {
        changeRequestId,
        billingGroupName: fccChange.entityName ?? familyName,
        entityIds: [fccChange.entityId],
        detail: {
          label: CaseTypeDisplay.FirstCapitalCallDate,
          currentValues: [fccChange.currentDate ? formatDate(fccChange.currentDate) : "--"],
          proposedValues: [fccChange.proposedDate ? formatDate(fccChange.proposedDate) : "--"],
        },
      };
      const existing = entityMap.get(fccChange.entityId) ?? [];
      existing.push(storedChange);
      entityMap.set(fccChange.entityId, existing);
      entityCounts.set(fccChange.entityId, (entityCounts.get(fccChange.entityId) ?? 0) + 1);
    }
    return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
  }

  if (requestType === "rename") {
    const proposedValues = caseDetail.proposedDisplayName
      ? [caseDetail.proposedDisplayName]
      : ["None"];
    const currentDisplayName = caseDetail.originalDisplayName ?? familyName;
    billingGroupChanges.set(familyName, [
      {
        changeRequestId,
        billingGroupName: familyName,
        billingGroupId: caseDetail.familyId ?? "",
        entityIds: [],
        detail: {
          label: "Billing group name",
          currentValues: [currentDisplayName],
          proposedValues,
        },
      },
    ]);
    familyCounts.set(familyName, 1);
    return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
  }

  if (requestType === "remove") {
    const currentFeederFunds = caseDetail.currentVsProposed?.currentFeederFunds ?? [];
    const currentEntityNames = currentFeederFunds
      .map((f) => f.name)
      .filter((name): name is string => name !== null);

    familyMap.set(familyName, {
      changeRequestId,
      familyId: caseDetail.familyId ?? "",
      familyName,
      currentEntities: currentEntityNames,
      proposedEntities: currentEntityNames.map(() => "--"),
    });
    familyCounts.set(familyName, 1);
    return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
  }

  const currentVsProposed = caseDetail.currentVsProposed;
  if (!currentVsProposed) {
    return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
  }

  const proposedAdded = currentVsProposed.proposedAdded ?? [];
  const currentFeederFunds = currentVsProposed.currentFeederFunds ?? [];
  const proposedRemoved = currentVsProposed.proposedRemoved ?? [];

  if (proposedAdded.length === 0 && proposedRemoved.length === 0) {
    return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
  }

  const familyId = caseDetail.familyId ?? "";

  const addedEntityIds = proposedAdded.map((f) => f.feederFundId);
  for (const fund of proposedAdded) {
    const storedChange: StoredProposedChange = {
      changeRequestId,
      billingGroupName: familyName,
      entityIds: addedEntityIds,
      detail: {
        label: "Billing group",
        currentValues: [fund.sourceFamilyDisplayName ?? NOT_ASSIGNED_LABEL],
        proposedValues: [familyName],
      },
    };
    const existing = entityMap.get(fund.feederFundId) ?? [];
    existing.push(storedChange);
    entityMap.set(fund.feederFundId, existing);
    entityCounts.set(fund.feederFundId, (entityCounts.get(fund.feederFundId) ?? 0) + 1);
  }

  const removedEntityIds = proposedRemoved.map((f) => f.feederFundId);
  for (const fund of proposedRemoved) {
    const storedChange: StoredProposedChange = {
      changeRequestId,
      billingGroupName: familyName,
      entityIds: removedEntityIds,
      detail: {
        label: "Billing group",
        currentValues: [familyName],
        proposedValues: [NOT_ASSIGNED_LABEL],
      },
    };
    const existing = entityMap.get(fund.feederFundId) ?? [];
    existing.push(storedChange);
    entityMap.set(fund.feederFundId, existing);
    entityCounts.set(fund.feederFundId, (entityCounts.get(fund.feederFundId) ?? 0) + 1);
  }

  const currentEntityNames = currentFeederFunds
    .map((f) => f.name)
    .filter((name): name is string => name !== null);
  const addedEntityNames = proposedAdded
    .map((f) => f.name)
    .filter((name): name is string => name !== null);
  const removedEntityNames = new Set(
    proposedRemoved.map((f) => f.name).filter((name): name is string => name !== null),
  );

  const proposedEntityNames = [
    ...currentEntityNames.filter((name) => !removedEntityNames.has(name)),
    ...addedEntityNames,
  ];

  const familyChange: StoredFamilyProposedChange = {
    changeRequestId,
    familyId,
    familyName,
    currentEntities: currentEntityNames,
    proposedEntities: proposedEntityNames,
  };

  familyMap.set(familyName, familyChange);
  familyCounts.set(familyName, 1);

  return { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges };
};

export const EntitiesTab = ({
  isChecker,
  clientId,
  onLocalChangesUpdate,
  onEntitiesActionsReady,
  pendingChangeRequestIds: pendingChangeRequestIdsProp,
  pendingGpChangeRequestIds: pendingGpChangeRequestIdsProp,
}: EntitiesTabProps) => {
  const pendingChangeRequestIds = pendingChangeRequestIdsProp ?? EMPTY_PENDING_IDS;
  const pendingGpChangeRequestIds = pendingGpChangeRequestIdsProp ?? EMPTY_PENDING_IDS;
  const isFCCEnabled = useFeatureFlag("billingFirstCapitalCallDate", false);
  const isSafeCaseIdEnabled = useFeatureFlag("billingSafeCaseIdAndActiveInvestments", false);
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [inspectionMode, setInspectionMode] = useState<"entity" | "family">("entity");
  const [inspectedEntityId, setInspectedEntityId] = useState<number | null>(null);
  const [inspectedEntityName, setInspectedEntityName] = useState<string | null>(null);
  const [inspectedFamilyId, setInspectedFamilyId] = useState<string | null>(null);
  const [inspectedFamilyName, setInspectedFamilyName] = useState<string | null>(null);
  const [proposedChangeMap, setProposedChangeMap] = useState<
    Map<number, StoredProposedChange[]>
  >(new Map());
  const [familyProposedChangeMap, setFamilyProposedChangeMap] = useState<
    Map<string, StoredFamilyProposedChange>
  >(new Map());
  const [initialProposedChanges, setInitialProposedChanges] = useState<Map<number, number>>(
    new Map(),
  );
  const [familyProposedChangeCounts, setFamilyProposedChangeCounts] = useState<
    Map<string, number>
  >(new Map());
  const [localPendingChanges, setLocalPendingChanges] = useState<BulkActionChangeData[]>([]);
  const [billingGroupProposedChanges, setBillingGroupProposedChanges] = useState<
    Map<string, StoredProposedChange[]>
  >(new Map());
  const [localFCCChanges, setLocalFCCChanges] = useState<PendingFCCChange[]>([]);
  const gridActionsRef = useRef<GridActions | null>(null);
  const hasFetchedCaseDetails = useRef(false);
  const localPendingChangesRef = useRef(localPendingChanges);
  const localFCCChangesRef = useRef(localFCCChanges);
  localPendingChangesRef.current = localPendingChanges;
  localFCCChangesRef.current = localFCCChanges;

  const [createChangeRequest] = useCreateChangeRequestMutation({
    onError: (error) => {
      const beExtensions = error.graphQLErrors[0]?.extensions;
      const errorCode = beExtensions?.error_code;
      const resolveExistingTypeLabel = () => {
        const rawExistingTypes = beExtensions?.existing_request_types;
        const existingTypes = Array.isArray(rawExistingTypes)
          ? rawExistingTypes.filter((t): t is string => typeof t === "string")
          : [];
        return existingTypes.length === 1
          ? (EXISTING_REQUEST_TYPE_LABELS[existingTypes[0]] ??
              EXISTING_REQUEST_TYPE_FALLBACK_LABEL)
          : EXISTING_REQUEST_TYPE_FALLBACK_LABEL;
      };
      if (errorCode === ENTITY_HAS_OPEN_CR_ERROR_CODE) {
        toast.error(
          `One or more selected entities already have an open ${resolveExistingTypeLabel()} for this client. Resolve the existing case before creating a new one.`,
        );
        return;
      }
      if (errorCode === FAMILY_HAS_OPEN_CR_ERROR_CODE) {
        toast.error(
          `An open ${resolveExistingTypeLabel()} already exists for this family. Resolve the existing case before creating a new one.`,
        );
        return;
      }
      toast.error(error.message);
    },
  });

  const [fetchCaseDetail] = useGetBillingCaseDetailLazyQuery({
    fetchPolicy: "network-only",
  });

  const [fetchGpChangeRequest] = useGetGpChangeRequestLazyQuery({
    fetchPolicy: "network-only",
  });

  const [submitGpChangeRequest] = useSubmitGpChangeRequestMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const [generatePresignedDocumentPutUrl] = useGeneratePresignedDocumentPutUrlMutation();

  useEffect(() => {
    if (
      (pendingChangeRequestIds.length === 0 && pendingGpChangeRequestIds.length === 0) ||
      hasFetchedCaseDetails.current
    ) {
      return;
    }
    hasFetchedCaseDetails.current = true;

    let cancelled = false;

    const fetchAll = async () => {
      const legacyPromises = pendingChangeRequestIds.map((crId) =>
        fetchCaseDetail({ variables: { changeRequestId: crId } }).then((result) => {
          const caseDetail = result.data?.getCaseDetail;
          if (!caseDetail) {
            return null;
          }
          return buildProposedChangesFromCaseDetail(crId, caseDetail);
        }),
      );

      const gpPromises = pendingGpChangeRequestIds.map((crId) =>
        fetchGpChangeRequest({ variables: { changeRequestId: crId } }).then((result) => {
          const gpCr = result.data?.getGpChangeRequest;
          if (!gpCr) {
            return null;
          }
          return buildProposedChangesFromGpChangeRequest(gpCr);
        }),
      );

      const results = await Promise.allSettled([...legacyPromises, ...gpPromises]);

      if (cancelled) {
        return;
      }

      const aggregatedEntityMap = new Map<number, StoredProposedChange[]>();
      const aggregatedEntityCounts = new Map<number, number>();
      const aggregatedFamilyMap = new Map<string, StoredFamilyProposedChange>();
      const aggregatedFamilyCounts = new Map<string, number>();
      const aggregatedBillingGroupChanges = new Map<string, StoredProposedChange[]>();

      for (const result of results) {
        if (result.status !== "fulfilled" || !result.value) {
          continue;
        }
        const { entityMap, entityCounts, familyMap, familyCounts, billingGroupChanges } =
          result.value;

        for (const [entityId, changes] of entityMap) {
          const existing = aggregatedEntityMap.get(entityId) ?? [];
          aggregatedEntityMap.set(entityId, [...existing, ...changes]);
        }
        for (const [entityId, count] of entityCounts) {
          aggregatedEntityCounts.set(
            entityId,
            (aggregatedEntityCounts.get(entityId) ?? 0) + count,
          );
        }
        for (const [name, change] of familyMap) {
          aggregatedFamilyMap.set(name, change);
        }
        for (const [name, count] of familyCounts) {
          aggregatedFamilyCounts.set(name, (aggregatedFamilyCounts.get(name) ?? 0) + count);
        }
        for (const [name, changes] of billingGroupChanges) {
          const existing = aggregatedBillingGroupChanges.get(name) ?? [];
          aggregatedBillingGroupChanges.set(name, [...existing, ...changes]);
        }
      }

      setProposedChangeMap(aggregatedEntityMap);
      setInitialProposedChanges(aggregatedEntityCounts);
      setFamilyProposedChangeMap(aggregatedFamilyMap);
      setFamilyProposedChangeCounts(aggregatedFamilyCounts);
      setBillingGroupProposedChanges(aggregatedBillingGroupChanges);
    };

    void fetchAll();

    return () => {
      cancelled = true;
      hasFetchedCaseDetails.current = false;
    };
  }, [
    pendingChangeRequestIds,
    pendingGpChangeRequestIds,
    fetchCaseDetail,
    fetchGpChangeRequest,
  ]);

  const hasPendingEumChangesValue = localPendingChanges.some(isEumPendingChange);

  useEffect(() => {
    onLocalChangesUpdate?.(
      localPendingChanges.length > 0 || localFCCChanges.length > 0,
      hasPendingEumChangesValue,
    );
  }, [
    localPendingChanges.length,
    localFCCChanges.length,
    hasPendingEumChangesValue,
    onLocalChangesUpdate,
  ]);

  const createAllPendingChanges = useCallback(async () => {
    const assignGroups = new Map<
      string,
      { entityIds: number[]; formValues: CreateChangeRequestData["formValues"] }
    >();

    const removeGroups = new Map<string, { entityIds: number[]; billingGroupId: string }>();
    const standaloneEntityIds: number[] = [];
    const renameChanges: Array<{
      billingGroupId: string;
      newBillingGroupName: string;
    }> = [];
    const removeGroupChanges: Array<{ billingGroupId: string }> = [];

    for (const change of localPendingChanges) {
      if ("formValues" in change) {
        const isExisting = change.formValues.assignmentType === "existing";
        const key = isExisting
          ? `existing:${change.formValues.fundFamily ?? ""}`
          : `new:${change.formValues.fundFamilyName}`;

        const existing = assignGroups.get(key);
        if (existing) {
          existing.entityIds.push(...change.entityIds);
        } else {
          assignGroups.set(key, {
            entityIds: [...change.entityIds],
            formValues: change.formValues,
          });
        }
      } else if (change.action === "remove") {
        const key = change.billingGroupId;
        const existing = removeGroups.get(key);
        if (existing) {
          existing.entityIds.push(...change.entityIds);
        } else {
          removeGroups.set(key, {
            entityIds: [...change.entityIds],
            billingGroupId: change.billingGroupId,
          });
        }
      } else if (change.action === "standalone") {
        standaloneEntityIds.push(...change.entityIds);
      } else if (change.action === "rename") {
        renameChanges.push({
          billingGroupId: change.billingGroupId,
          newBillingGroupName: change.newBillingGroupName,
        });
      } else if (change.action === "remove_group") {
        removeGroupChanges.push({
          billingGroupId: change.billingGroupId,
        });
      }
    }

    const addAction: FamilyChangeRequestItemAction = "add";
    const removeAction: FamilyChangeRequestItemAction = "remove";

    const mutations = [];

    for (const group of assignGroups.values()) {
      const isExisting = group.formValues.assignmentType === "existing";
      const input = {
        clientId,
        items: [{ feederFundIds: group.entityIds, action: addAction }],
        ...(isExisting
          ? { familyId: group.formValues.fundFamily }
          : { displayName: group.formValues.fundFamilyName }),
        ...(isSafeCaseIdEnabled && { sfSafeCaseId: group.formValues.safeCaseId }),
      };
      mutations.push(createChangeRequest({ variables: { input } }));
    }

    for (const group of removeGroups.values()) {
      const input = {
        clientId,
        familyId: group.billingGroupId,
        items: [{ feederFundIds: group.entityIds, action: removeAction }],
      };
      mutations.push(createChangeRequest({ variables: { input } }));
    }

    if (standaloneEntityIds.length > 0) {
      const input = {
        clientId,
        targetStandalone: true,
        items: [{ feederFundIds: standaloneEntityIds, action: addAction }],
      };
      mutations.push(createChangeRequest({ variables: { input } }));
    }

    for (const rename of renameChanges) {
      const input = {
        clientId,
        familyId: rename.billingGroupId,
        displayName: rename.newBillingGroupName,
        requestType: BillingChangeRequestType.Rename,
      };
      mutations.push(createChangeRequest({ variables: { input } }));
    }

    for (const removeGroup of removeGroupChanges) {
      const input = {
        clientId,
        familyId: removeGroup.billingGroupId,
        requestType: BillingChangeRequestType.Remove,
      };
      mutations.push(createChangeRequest({ variables: { input } }));
    }

    if (isFCCEnabled) {
      for (const fccChange of localFCCChanges) {
        const input = {
          clientId,
          requestType: BillingChangeRequestType.FirstCapitalCallDate,
          firstCapitalCallDateItems: {
            entityIds: fccChange.entityIds,
            proposedDate: fccChange.proposedDate,
          },
        };
        mutations.push(createChangeRequest({ variables: { input } }));
      }
    }

    const mutationResults = await Promise.allSettled(mutations);

    const results: Array<{
      changeRequestId: string;
      billingCaseId: number;
      status: CaseStatus;
    }> = [];

    for (const outcome of mutationResults) {
      if (outcome.status !== "fulfilled") {
        continue;
      }
      const cr = outcome.value?.data?.createChangeRequest?.changeRequest;
      if (cr) {
        results.push({
          changeRequestId: cr.id,
          billingCaseId: cr.billingCaseId,
          status: cr.status,
        });
      }
    }

    setLocalPendingChanges((prev) => prev.filter((change) => isEumPendingChange(change)));
    if (isFCCEnabled) {
      setLocalFCCChanges([]);
    }
    return results;
  }, [localPendingChanges, localFCCChanges, clientId, createChangeRequest, isFCCEnabled]);

  const submitAllPendingEumChanges = useCallback(
    async ({
      billingApproverId,
      csmManagerApproverId,
    }: SubmitGpEumChangesArgs): Promise<SubmittedPendingChange[]> => {
      const eumChanges = localPendingChanges.filter(isEumPendingChange);
      if (eumChanges.length === 0) {
        return [];
      }

      const snapshotTakenAt = new Date().toISOString();
      const items: EumChangeItemInput[] = [];
      for (const change of eumChanges) {
        const file = change.file;
        let s3DocumentKey: string | null = null;
        if (file) {
          try {
            s3DocumentKey = await uploadGpSupportingDocument({
              file,
              clientId,
              generatePresignedPutUrl: generatePresignedDocumentPutUrl,
            });
          } catch (uploadError) {
            toast.error(
              uploadError instanceof Error
                ? uploadError.message
                : "Supporting document upload failed",
            );
            throw uploadError;
          }
        }

        if (change.action === "eum_value_override") {
          items.push(buildEumOverrideItem({ change, s3DocumentKey, snapshotTakenAt }));
        } else {
          items.push(buildEumCalcEditItem({ change, s3DocumentKey, snapshotTakenAt }));
        }
      }

      const submission = await submitGpChangeRequest({
        variables: {
          input: {
            clientId,
            items,
            billingApproverId,
            csmManagerApproverId,
          },
        },
      });

      const cr = submission.data?.submitGpChangeRequest?.changeRequest;
      if (!cr) {
        return [];
      }

      setLocalPendingChanges((prev) =>
        prev.filter((change) => !eumChanges.some((submitted) => submitted === change)),
      );

      return [
        {
          changeRequestId: cr.id,
          billingCaseId: cr.billingCaseId,
          status: "IN_REVIEW",
        },
      ];
    },
    [localPendingChanges, clientId, submitGpChangeRequest, generatePresignedDocumentPutUrl],
  );

  useEffect(() => {
    onEntitiesActionsReady?.({
      createAllPendingChanges,
      submitAllPendingEumChanges,
    });
  }, [createAllPendingChanges, submitAllPendingEumChanges, onEntitiesActionsReady]);

  const handleEntityProposedChangeClick = useCallback(
    (entityId: number, entityName: string | null) => {
      const changes = proposedChangeMap.get(entityId);
      if (changes && changes.length > 0) {
        setInspectionMode("entity");
        setInspectedEntityId(entityId);
        setInspectedEntityName(entityName);
        setInspectedFamilyId(null);
        setInspectedFamilyName(null);
        setIsInspectionModalOpen(true);
      }
    },
    [proposedChangeMap],
  );

  const handleFamilyProposedChangeClick = useCallback(
    (familyName: string | null) => {
      if (!familyName) {
        return;
      }
      const localChanges = billingGroupProposedChanges.get(familyName);
      if (localChanges && localChanges.length > 0) {
        setInspectionMode("entity");
        setInspectedFamilyId(familyName);
        setInspectedFamilyName(familyName);
        setInspectedEntityId(null);
        setInspectedEntityName(null);
        setIsInspectionModalOpen(true);
        return;
      }
      const change = familyProposedChangeMap.get(familyName);
      if (change) {
        setInspectionMode("family");
        setInspectedFamilyId(familyName);
        setInspectedFamilyName(familyName);
        setInspectedEntityId(null);
        setInspectedEntityName(null);
        setIsInspectionModalOpen(true);
      }
    },
    [familyProposedChangeMap, billingGroupProposedChanges],
  );

  const handleInspectionClose = useCallback(() => {
    setIsInspectionModalOpen(false);
    setInspectedEntityId(null);
    setInspectedEntityName(null);
    setInspectedFamilyId(null);
    setInspectedFamilyName(null);
  }, []);

  const wouldMixChangeTypes = useCallback(
    (incomingIsEum: boolean): boolean =>
      evaluateChangeTypeMixing(
        localPendingChangesRef.current,
        localFCCChangesRef.current,
        incomingIsEum,
      ),
    [],
  );

  const handleProposedChangeSubmit = useCallback(
    (data: BulkActionChangeData): Promise<{ familyName: string } | null> => {
      setLocalPendingChanges((prev) => [...prev, data]);
      if ("familyDisplayName" in data) {
        return Promise.resolve({ familyName: data.familyDisplayName });
      }
      if ("action" in data && data.action === "rename") {
        setFamilyProposedChangeCounts((prev) => {
          const next = new Map(prev);
          next.set(data.billingGroupName, (next.get(data.billingGroupName) ?? 0) + 1);
          return next;
        });
        setBillingGroupProposedChanges((prev) => {
          const next = new Map(prev);
          const existing = next.get(data.billingGroupName) ?? [];
          next.set(data.billingGroupName, [
            ...existing,
            {
              changeRequestId: "",
              billingGroupName: data.billingGroupName,
              billingGroupId: data.billingGroupId,
              entityIds: [],
              detail: {
                label: "Billing group name",
                currentValues: [data.billingGroupName],
                proposedValues: [data.newBillingGroupName],
              },
            },
          ]);
          return next;
        });
      }
      if ("action" in data && data.action === "remove_group") {
        setFamilyProposedChangeCounts((prev) => {
          const next = new Map(prev);
          next.set(data.billingGroupName, (next.get(data.billingGroupName) ?? 0) + 1);
          return next;
        });
        setFamilyProposedChangeMap((prev) => {
          const next = new Map(prev);
          next.set(data.billingGroupName, {
            changeRequestId: "",
            familyId: data.billingGroupId,
            familyName: data.billingGroupName,
            currentEntities: data.entityNames,
            proposedEntities: data.entityNames.map(() => "--"),
          });
          return next;
        });
      }
      return Promise.resolve(null);
    },
    [],
  );

  const handleEditFirstCapitalCallDateSubmit = useCallback(
    (entityIds: number[], entityNames: string[], date: Date) => {
      setLocalFCCChanges((prev) => [
        ...prev,
        { entityIds, entityNames, proposedDate: formatDateForApi(date) },
      ]);
    },
    [],
  );

  const handleProposedChangeDetail = useCallback(
    (entityIds: number[], change: StoredProposedChange) => {
      setProposedChangeMap((prev) => {
        const next = new Map(prev);
        for (const id of entityIds) {
          const existing = next.get(id) ?? [];
          next.set(id, [...existing, { ...change, entityIds: [id] }]);
        }
        return next;
      });
    },
    [],
  );

  const handleGridActionsReady = useCallback((actions: GridActions) => {
    gridActionsRef.current = actions;
  }, []);

  const handleDeleteProposedChange = useCallback(
    (changeIndex: number) => {
      if (inspectedFamilyId !== null && inspectedEntityId === null) {
        const changes = billingGroupProposedChanges.get(inspectedFamilyId);
        if (changes && changes.length > 0 && changeIndex < changes.length) {
          const removedChange = changes[changeIndex];

          setLocalPendingChanges((prev) =>
            prev.filter((pendingChange) => {
              if (!("action" in pendingChange)) return true;
              if (pendingChange.action !== "rename") return true;
              return pendingChange.billingGroupId !== removedChange.billingGroupId;
            }),
          );

          setBillingGroupProposedChanges((prev) => {
            const next = new Map(prev);
            const filtered = (next.get(inspectedFamilyId) ?? []).filter(
              (_, i) => i !== changeIndex,
            );
            if (filtered.length === 0) {
              next.delete(inspectedFamilyId);
            } else {
              next.set(inspectedFamilyId, filtered);
            }
            return next;
          });

          setFamilyProposedChangeCounts((prev) => {
            const next = new Map(prev);
            const currentCount = next.get(inspectedFamilyId) ?? 0;
            if (currentCount <= 1) {
              next.delete(inspectedFamilyId);
            } else {
              next.set(inspectedFamilyId, currentCount - 1);
            }
            return next;
          });

          if (changes.length - 1 <= 0) {
            handleInspectionClose();
          }
          return;
        }

        const familyChange = familyProposedChangeMap.get(inspectedFamilyId);
        if (familyChange) {
          setLocalPendingChanges((prev) =>
            prev.filter((pendingChange) => {
              if (!("action" in pendingChange)) return true;
              if (pendingChange.action !== "remove_group") return true;
              return pendingChange.billingGroupId !== familyChange.familyId;
            }),
          );

          setFamilyProposedChangeMap((prev) => {
            const next = new Map(prev);
            next.delete(inspectedFamilyId);
            return next;
          });

          setFamilyProposedChangeCounts((prev) => {
            const next = new Map(prev);
            next.delete(inspectedFamilyId);
            return next;
          });

          handleInspectionClose();
          return;
        }

        return;
      }

      if (inspectedEntityId === null) {
        return;
      }
      const changes = proposedChangeMap.get(inspectedEntityId);
      if (!changes || changeIndex >= changes.length) {
        return;
      }
      const removedChange = changes[changeIndex];

      const deletedEntityId = removedChange.entityIds[0];
      setLocalPendingChanges((prev) => {
        const next: BulkActionChangeData[] = [];
        for (const pc of prev) {
          if (!("entityIds" in pc)) {
            next.push(pc);
            continue;
          }

          if (!pc.entityIds.includes(deletedEntityId)) {
            next.push(pc);
            continue;
          }

          let isMatch = false;
          if ("familyDisplayName" in pc) {
            isMatch = pc.familyDisplayName === removedChange.billingGroupName;
          } else if ("action" in pc && pc.action === "remove") {
            isMatch = pc.billingGroupName === removedChange.billingGroupName;
          } else if ("action" in pc && pc.action === "standalone") {
            isMatch = true;
          } else if ("action" in pc && pc.action === "eum_value_override") {
            isMatch = removedChange.detail.label === EUM_VALUE_OVERRIDE_ROW_LABEL;
          } else if ("action" in pc && pc.action === "eum_calc_edit") {
            isMatch = removedChange.detail.label === EUM_CALCULATION_VALUES_ROW_LABEL;
          }

          if (!isMatch) {
            next.push(pc);
            continue;
          }

          const remainingIds = pc.entityIds.filter((id: number) => id !== deletedEntityId);
          const remainingNames = pc.entityNames.filter(
            (_: string, idx: number) => pc.entityIds[idx] !== deletedEntityId,
          );
          if (remainingIds.length > 0) {
            next.push({ ...pc, entityIds: remainingIds, entityNames: remainingNames });
          }
        }
        return next;
      });

      const affectedIds = removedChange.entityIds;
      let remainingForInspected = 0;
      setProposedChangeMap((prev) => {
        const next = new Map(prev);
        for (const id of affectedIds) {
          const entityChanges = next.get(id);
          if (!entityChanges) {
            continue;
          }
          const filtered = entityChanges.filter((c) => c !== removedChange);
          if (filtered.length === 0) {
            next.delete(id);
          } else {
            next.set(id, filtered);
          }
          if (id === inspectedEntityId) {
            remainingForInspected = filtered.length;
          }
        }
        return next;
      });

      if (removedChange.detail.label === CaseTypeDisplay.FirstCapitalCallDate) {
        setLocalFCCChanges((prev) =>
          prev.filter(
            (fcc) => !removedChange.entityIds.every((id) => fcc.entityIds.includes(id)),
          ),
        );
      }

      gridActionsRef.current?.deleteProposedChanges(affectedIds);

      if (remainingForInspected <= 0) {
        handleInspectionClose();
      }
    },
    [
      inspectedEntityId,
      inspectedFamilyId,
      proposedChangeMap,
      billingGroupProposedChanges,
      familyProposedChangeMap,
      handleInspectionClose,
    ],
  );

  const inspectedFamilyChange = useMemo(() => {
    if (inspectedFamilyId === null) {
      return undefined;
    }
    return familyProposedChangeMap.get(inspectedFamilyId);
  }, [inspectedFamilyId, familyProposedChangeMap]);

  const modalSubtitle = useMemo(() => {
    if (inspectionMode === "family") {
      return inspectedFamilyName ?? inspectedFamilyChange?.familyName;
    }
    if (inspectedEntityId !== null) {
      return (
        inspectedEntityName ?? proposedChangeMap.get(inspectedEntityId)?.[0]?.billingGroupName
      );
    }
    return inspectedFamilyName ?? undefined;
  }, [
    inspectionMode,
    inspectedFamilyName,
    inspectedFamilyChange,
    inspectedEntityName,
    inspectedEntityId,
    proposedChangeMap,
  ]);

  return (
    <Flex flexDirection="column" className={styles.tabContentContainer}>
      <Text variant="h3" className={styles.entitiesTitle}>
        Entities
      </Text>
      <CaseEntitiesGrid
        isChecker={isChecker}
        clientId={String(clientId)}
        onEntityProposedChangeClick={handleEntityProposedChangeClick}
        onFamilyProposedChangeClick={handleFamilyProposedChangeClick}
        onProposedChangeSubmit={handleProposedChangeSubmit}
        onProposedChangeDetail={handleProposedChangeDetail}
        onEditFirstCapitalCallDateSubmit={handleEditFirstCapitalCallDateSubmit}
        wouldMixChangeTypes={wouldMixChangeTypes}
        onGridActionsReady={handleGridActionsReady}
        initialProposedChanges={initialProposedChanges}
        familyProposedChangeCounts={familyProposedChangeCounts}
      />
      {inspectionMode === "entity" ? (
        <ChangeInspectionModal
          mode="entity"
          isOpen={isInspectionModalOpen}
          onClose={handleInspectionClose}
          onDeleteChange={isChecker ? undefined : handleDeleteProposedChange}
          readOnly={isChecker}
          changes={
            inspectedEntityId !== null
              ? (proposedChangeMap.get(inspectedEntityId) ?? [])
              : inspectedFamilyId !== null
                ? (billingGroupProposedChanges.get(inspectedFamilyId) ?? [])
                : []
          }
          subtitle={modalSubtitle}
        />
      ) : (
        <ChangeInspectionModal
          mode="family"
          isOpen={isInspectionModalOpen}
          onClose={handleInspectionClose}
          onDeleteChange={isChecker ? undefined : handleDeleteProposedChange}
          readOnly={isChecker}
          familyChange={inspectedFamilyChange!}
          subtitle={modalSubtitle}
        />
      )}
    </Flex>
  );
};
